// @flow
/* eslint-disable import/prefer-default-export */
import * as at from 'actions/action-types/inventory';
import {
  type ItemShipmentStockRecord,
  type AllocationStrategy,
  type AllocationRecord,
  type WarehouseId,
  type StockShipmentsRecord,
  stockShipmentsRecordsFromItemShipmentStockRecords,
} from 'modules/inventory';
import {
  stockItemCompoundKey,
  stockItemComponents,
  isComponentsInventoryMode,
} from 'modules/product';
import {
  type PageProduct,
  type PageItem,
  allocations,
} from 'modules/page-product';

export type FSA<T, P = void> = $ReadOnly<{|
  type: T,
  payload: P,
|}>;

export type UpdateStockForStockItems = FSA<
  typeof at.UPDATE_STOCK_FOR_STOCK_ITEMS,
  $ReadOnlyArray<StockShipmentsRecord>,
>;

export type AllocateStockForStockItems = FSA<
  typeof at.ALLOCATE_STOCK_FOR_STOCK_ITEMS,
  $ReadOnlyArray<AllocationRecord>,
>;

export type ClearAllocations = FSA<
  typeof at.CLEAR_ALLOCATIONS,
  $ReadOnlyArray<{|
    +stockItemKey: string,
    +pageItemIds: $ReadOnlyArray<string>,
  |}>,
>;

export type ClearConstrainedAllocations = FSA<
  typeof at.CLEAR_CONSTRAINED_ALLOCATIONS,
  void,
>;

export type ClearInventory = FSA<typeof at.CLEAR_INVENTORY, void>;

export type Action =
  | UpdateStockForStockItems
  | AllocateStockForStockItems
  | ClearAllocations
  | ClearConstrainedAllocations
  | ClearInventory;
export const updateStockForStockItems = (
  payload: $ReadOnlyArray<StockShipmentsRecord>,
): UpdateStockForStockItems => ({
  type: at.UPDATE_STOCK_FOR_STOCK_ITEMS,
  payload,
});

export const allocateStockForStockItems = (
  payload: $ReadOnlyArray<AllocationRecord>,
): AllocateStockForStockItems => ({
  type: at.ALLOCATE_STOCK_FOR_STOCK_ITEMS,
  payload,
});

export const clearAllocations = (
  payload: $ReadOnlyArray<{|
    +stockItemKey: string,
    +pageItemIds: $ReadOnlyArray<string>,
  |}>,
): ClearAllocations => ({
  type: at.CLEAR_ALLOCATIONS,
  payload,
});

export const clearConstrainedAllocations = (): ClearConstrainedAllocations => ({
  type: at.CLEAR_CONSTRAINED_ALLOCATIONS,
  payload: undefined,
});

export const clearInventory = (): ClearInventory => ({
  type: at.CLEAR_INVENTORY,
  payload: undefined,
});

export const updateStockShipments = (
  stockShipments: $ReadOnlyArray<ItemShipmentStockRecord>,
  fulfillmentStrategyForStockShipment: (ItemShipmentStockRecord) => AllocationStrategy,
  releaseDateForStockShipment: (ItemShipmentStockRecord) => Date | null,
): UpdateStockForStockItems =>
  updateStockForStockItems(
    stockShipmentsRecordsFromItemShipmentStockRecords(
      fulfillmentStrategyForStockShipment,
      releaseDateForStockShipment,
    )(stockShipments),
  );

export const allocateStockForPageItem = (
  pageProduct: PageProduct,
  pageItem: PageItem,
  warehouseId?: WarehouseId | null,
): AllocateStockForStockItems =>
  allocateStockForStockItems(allocations(pageProduct, pageItem, warehouseId));

export const allocateStockForPageProduct = (
  pageProduct: PageProduct,
  warehouseId?: WarehouseId | null,
): AllocateStockForStockItems =>
  allocateStockForStockItems(
    pageProduct.pageItems.flatMap((pi) =>
      allocations(pageProduct, pi, warehouseId),
    ),
  );

export const allocateStockForPageProducts = (
  pageProducts: $ReadOnlyArray<PageProduct>,
  warehouseId?: WarehouseId | null,
): AllocateStockForStockItems =>
  allocateStockForStockItems(
    pageProducts.flatMap((pageProduct) =>
      pageProduct.pageItems.flatMap((pi) =>
        allocations(pageProduct, pi, warehouseId),
      ),
    ),
  );

export const clearAllocationsForPageProduct = (
  pageProduct: PageProduct,
): ClearAllocations =>
  clearAllocations(
    pageProduct.pageItems.flatMap((pi) =>
      isComponentsInventoryMode(pi.stockItem)
        ? stockItemComponents(pi.stockItem).map((component) => ({
            pageItemIds: [pi.uniqueId],
            stockItemKey: stockItemCompoundKey(pageProduct.product, component),
          }))
        : [
            {
              stockItemKey: stockItemCompoundKey(
                pageProduct.product,
                pi.stockItem,
              ),
              pageItemIds: [pi.uniqueId],
            },
          ],
    ),
  );

export const clearAllocationsForPageProducts = (
  pageProducts: $ReadOnlyArray<PageProduct>,
): ClearAllocations =>
  clearAllocations(
    pageProducts.flatMap((pageProduct) =>
      pageProduct.pageItems.flatMap((pi) =>
        isComponentsInventoryMode(pi.stockItem)
          ? stockItemComponents(pi.stockItem).map((component) => ({
              pageItemIds: [pi.uniqueId],
              stockItemKey: stockItemCompoundKey(
                pageProduct.product,
                component,
              ),
            }))
          : [
              {
                stockItemKey: stockItemCompoundKey(
                  pageProduct.product,
                  pi.stockItem,
                ),
                pageItemIds: [pi.uniqueId],
              },
            ],
      ),
    ),
  );
